<template>
  <div class="fluid-details new-globals">
    <div class="fluid-info-container">
      <md-field class="ui-theme-3 detail-field name-field">
        <label>Fluid</label>
        <md-input :value="fluid.label.en" disabled />
      </md-field>
      <md-field class="ui-theme-3 detail-field description-field">
        <label>Description</label>
        <md-textarea :value="caseFluid ? caseFluid.description : null" :disabled="isReadOnly" :maxlength="500"
                     @input="description => updateDescription(description)"
        />
      </md-field>
      <div class="dosing-type-container">
        <md-field class="ui-theme-3 detail-field">
          <label>Dosings</label>
          <infusion-dosing-type-select
            :value="caseFluid ? caseFluid.infusionDosingTypes : []"
            :allowable-dosing-types="availableDosingTypes"
            :disabled="isReadOnly"
            @input="dosingTypeKeys => updateDosingTypes(dosingTypeKeys)"
          />
        </md-field>
        <help-tooltip v-if="dosingTypeError" type="error">
          {{ dosingTypeError.message }}
        </help-tooltip>
      </div>
      <div class="bag-volume-container">
        <md-field class="ui-theme-3 detail-field">
          <label>Volumes</label>
          <infusion-bag-volume-select
            :value="caseFluid ? caseFluid.infusionBagVolumes : []"
            :disabled="isReadOnly"
            @input="bagVolumeKeys => updateBagVolumes(bagVolumeKeys)"
          />
        </md-field>
        <help-tooltip v-if="bagSizeError" type="error">
          {{ bagSizeError.message }}
        </help-tooltip>
      </div>
    </div>
    <md-divider />
    <div v-if="!isReadOnly" class="formulary-container">
      <div>
        <span class="md-title">Formulary</span>
      </div>
      <div>
        <md-button
          class="md-primary md-outlined-button add-route"
          :disabled="!canAddDosing"
          @click="addFormulary()"
        >
          <md-icon>add</md-icon>
          Add New Dosing
        </md-button>
      </div>
      <div v-for="(formulary, index) in caseFluid.formularies" :key="index" class="formulary-form-container">
        <md-button class="md-icon-button md-primary md-raised md-dense" @click="removeFormulary(index)">
          <md-icon>remove</md-icon>
        </md-button>
        <formulary-form
          :formulary="formulary"
          :volumes="caseFluid.infusionBagVolumes"
          :allowable-dosing-types="caseFluid.infusionDosingTypes"
          :errors="formularyErrors(index)"
          @update="formulary => updateFormulary(index, formulary)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';

import { infusionBagVolumes, infusionDosingTypes } from '~lib/common/config/medication';
import InfusionDosingTypeSelect from '~lib/common/components/medications/InfusionDosingTypeSelect';
import InfusionBagVolumeSelect from '~lib/common/components/medications/InfusionBagVolumeSelect';
import FormularyForm from '../FormularyForm';
import HelpTooltip from '~lib/common/components/HelpTooltip.vue';

export default {
  name: 'case-fluid-details',
  components: {
    InfusionDosingTypeSelect,
    InfusionBagVolumeSelect,
    FormularyForm,
    HelpTooltip
  },
  props: {
    fluid: {
      type: Object,
      required: true,
    },
    caseFluids: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    caseFluid() {
      return this.caseFluids.find(cf => cf.key === this.fluid.key);
    },
    isReadOnly() {
      return this.readonly || !this.caseFluid;
    },
    availableBagVolumes() {
      return infusionBagVolumes.filter(
        ibv => this.fluid.availableBagVolumes.find(abv => abv.includes(ibv.key))
      );
    },
    availableDosingTypes() {
      return infusionDosingTypes
        .filter(dt => dt.key.startsWith('ML_'))
        .map(dt => dt.key);
    },
    canAddDosing() {
      return this.caseFluid.infusionDosingTypes && this.caseFluid.infusionDosingTypes.length > 0 &&
             this.caseFluid.infusionBagVolumes && this.caseFluid.infusionBagVolumes.length > 0;
    },
    fluidErrors() {
      let errors = []
      if (this.$store && this.caseFluid) {
        errors = get(this.$store.getters['validation/errorsByFieldAndId'], `fluids.${this.caseFluid.id}`, []);
      }
      return errors;
    },
    dosingTypeError() {
      return this.fluidErrors.find(e => e.metadata.propertyName === 'infusionDosingTypes');
    },
    bagSizeError() {
      return this.fluidErrors.find(e => e.metadata.propertyName === 'infusionBagVolumes');
    },
  },
  methods: {
    updateDescription(description) {
      if (this.caseFluid) {
        const newCaseFluid = {
          ...this.caseFluid,
          description
        };
        this.emitUpdate(newCaseFluid);
      }
    },
    updateDosingTypes(dosingTypeKeys) {
      if (this.caseFluid) {
        const newCaseFluid = {
          ...this.caseFluid,
          infusionDosingTypes: dosingTypeKeys
        }
        this.emitUpdate(newCaseFluid);
      }
    },
    updateBagVolumes(volumes) {
      if (this.caseFluid) {
        const newCaseFluid = {
          ...this.caseFluid,
          infusionBagVolumes: volumes
        }
        this.emitUpdate(newCaseFluid);
      }
    },
    addFormulary() {
      if (this.caseFluid) {
        if (!this.caseFluid.infusionBagVolumes) {
          return;
        }
        const newCaseFluid = {
          ...this.caseFluid,
          formularies: (this.caseFluid.formularies || []).concat({
            dose: 1,
            dosing: this.caseFluid.infusionDosingTypes[0],
            volume: this.caseFluid.infusionBagVolumes[0]
          })
        }
        this.emitUpdate(newCaseFluid);
      }
    },
    updateFormulary(indexToUpdate, updatedFormulary) {
      if (this.caseFluid) {
        const newCaseFluid = {
          ...this.caseFluid,
          formularies: (this.caseFluid.formularies || []).map((formulary, index) => index === indexToUpdate ? updatedFormulary : formulary)
        }
        this.emitUpdate(newCaseFluid);
      }
    },
    removeFormulary(indexToRemove) {
      if (this.caseFluid) {
        const newCaseFluid = {
          ...this.caseFluid,
          formularies: (this.caseFluid.formularies || []).filter((formulary, index) => index !== indexToRemove)
        }
        this.emitUpdate(newCaseFluid);
      }
    },
    emitUpdate(caseFluid) {
      const updatedCaseFluids = cloneDeep(this.caseFluids);
      const fluidIndex = updatedCaseFluids.findIndex(cf => cf.key === this.fluid.key);
      if (fluidIndex >= 0) {
        updatedCaseFluids.splice(fluidIndex, 1, caseFluid);
        this.$emit('updateCase', { fluids: updatedCaseFluids });
      }
    },
    formularyErrors(index) {
      return this.fluidErrors.filter(e => e.metadata.propertyName.startsWith(`formulary[${index}]`))
    },
  },
}
</script>

<style scoped>
.fluid-info-container {
  flex: 1;
  display: grid;
  grid-template-rows: repeat(2, 1fr);
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 1rem;
  padding-bottom: 24px;
}

.detail-field {
  margin: 0 !important;
}

.description-field {
  grid-row-start: span 2;
}

.dosing-type-container,
.bag-volume-container {
  display: flex;
}

.formulary-container {
  padding-top: 8px;
}

.formulary-form-container {
  display: flex;
  align-items: center;
  padding-top: 4px;
  padding-bottom: 4px;
}
</style>